import React, { useState, useEffect, useMemo } from 'react';
import { Plus, Trash2, Send, Loader2, Search } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { API } from '../../utils/kitchenTasks';
import { todayZurich } from '../../utils/dateFormat';
import { formatApiError } from '../../services/api';

const fieldCls = 'h-11 rounded-xl bg-gray-100 border-gray-200 text-gray-900';

// Collaborator draft editor: product picker + quantities + note. Saves as brouillon; optional direct submit.
const OrderRequestForm = ({ open, onOpenChange, request, onSaved }) => {
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [date, setDate] = useState(todayZurich());
  const [note, setNote] = useState('');
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/orders/products`).then(r => setProducts(r.data.data || r.data || [])).catch(() => {});
    setDate(request?.date || todayZurich()); setNote(request?.note || ''); setItems(request?.items || []); setSearch(''); setBusy(false);
  }, [open, request]);

  const filtered = useMemo(() => products.filter(p => p.name.toLowerCase().includes(search.toLowerCase())).slice(0, 12), [products, search]);
  const addProduct = (p) => {
    if (items.some(i => i.product_id === p.id)) return;
    setItems([...items, { product_id: p.id, product_name: p.name, category: p.category || '', supplier: p.supplier || '', quantity: 1, unit: p.unit || 'kg', note: '' }]);
    setSearch('');
  };
  const update = (pid, field, value) => setItems(items.map(i => (i.product_id === pid ? { ...i, [field]: value } : i)));

  const save = async (submit) => {
    if (busy) return;
    const clean = items.map(i => ({ ...i, quantity: parseFloat(i.quantity) })).filter(i => i.quantity > 0);
    if (!clean.length) { toast.error('Ajoutez au moins un produit avec une quantité'); return; }
    setBusy(true);
    try {
      const payload = { date, items: clean, note, supplier: clean[0].supplier || '' };
      const res = request ? await axios.patch(`${API}/order-requests/${request.id}`, payload) : await axios.post(`${API}/order-requests`, payload);
      let saved = res.data.data;
      if (submit) saved = (await axios.post(`${API}/order-requests/${saved.id}/submit`)).data.data;
      toast.success(submit ? 'Demande envoyée au chef' : 'Brouillon enregistré');
      onSaved?.(saved); onOpenChange(false);
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!busy) onOpenChange(v); }}>
      <DialogContent className="bg-white border-gray-200 max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="order-request-form">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-gray-900">{request ? 'Modifier ma demande' : 'Nouvelle demande de commande'}</DialogTitle>
          <DialogDescription className="text-gray-500">Sélectionnez les produits nécessaires à votre poste. Le chef consolidera les demandes.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label className="text-gray-700">Date de livraison souhaitée</Label><input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={`mt-1 block w-full px-3 text-sm border ${fieldCls}`} data-testid="request-date-input" /></div>
            <div><Label className="text-gray-700">Remarque</Label><Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ex : urgent pour le service du soir" className={`mt-1 ${fieldCls}`} data-testid="request-note-input" /></div>
          </div>
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher un produit à ajouter…" className={`pl-9 ${fieldCls}`} data-testid="request-product-search" />
            {search && filtered.length > 0 && (
              <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg max-h-64 overflow-y-auto" data-testid="request-product-results">
                {filtered.map(p => (
                  <button key={p.id} type="button" onClick={() => addProduct(p)} className="w-full text-left px-3 py-2.5 min-h-[44px] hover:bg-gray-50 text-sm flex justify-between" data-testid={`request-product-${p.id}`}>
                    <span className="text-gray-900">{p.name}</span><span className="text-gray-400 text-xs">{p.category} · {p.unit}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="space-y-2" data-testid="request-items">
            {items.length === 0 && <p className="text-sm text-gray-400 text-center py-4">Aucun produit ajouté.</p>}
            {items.map(i => (
              <div key={i.product_id} className="flex items-center gap-2 border border-gray-200 rounded-xl px-3 py-2" data-testid={`request-item-${i.product_id}`}>
                <div className="flex-1 min-w-0"><p className="text-sm font-medium text-gray-900 truncate">{i.product_name}</p><p className="text-xs text-gray-400">{i.category}</p></div>
                <Input type="number" inputMode="decimal" step="any" min="0" value={i.quantity} onChange={(e) => update(i.product_id, 'quantity', e.target.value)} className="w-20 h-10 rounded-lg bg-gray-100 border-gray-200 text-right" data-testid={`request-qty-${i.product_id}`} />
                <span className="text-xs text-gray-500 w-8">{i.unit}</span>
                <button type="button" onClick={() => setItems(items.filter(x => x.product_id !== i.product_id))} className="w-10 h-10 rounded-lg text-red-600 hover:bg-red-50 flex items-center justify-center" data-testid={`request-remove-${i.product_id}`}><Trash2 size={16} /></button>
              </div>
            ))}
          </div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 pt-2">
          <Button variant="outline" onClick={() => save(false)} disabled={busy} className="flex-1 h-12 rounded-full border-gray-300" data-testid="request-save-draft-btn">{busy ? <Loader2 size={16} className="animate-spin mr-2" /> : <Plus size={16} className="mr-2" />}Enregistrer le brouillon</Button>
          <Button onClick={() => save(true)} disabled={busy} className="flex-1 h-12 rounded-full bg-neon-pink text-gray-900 font-semibold" data-testid="request-submit-btn">{busy ? <Loader2 size={16} className="animate-spin mr-2" /> : <Send size={16} className="mr-2" />}Envoyer au chef</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default OrderRequestForm;
